import React from 'react';
import PropTypes from 'prop-types';

// Style and SEO
import { Col, Grid, Row, styled } from '@smooth-ui/core-sc';
import Helmet from 'react-helmet';

import { withSettings } from '../../containers/WebApp/SettingsContext';
import PrivateComponent from '_platform/src/utils/PrivateComponent';

import IconWrapper from '_platform/src/components/SvgIcon/IconWrapper';
import LoadingAnimation from '_platform/src/components/Loading/LoadingAnimation';

import BannerHome from '../../components/CustomComponents/Backgrounds/BannerHome';
import BgSecondary from '../../components/CustomComponents/Backgrounds/BgSecondary';

import bannerMobile from './images/hp-home-banner-mobile-@1x.jpg';
import bannerMobile2x from './images/hp-home-banner-mobile-@2x.jpg';
import bannerTablet from './images/hp-home-banner-tablet-@1x.jpg';
import bannerTablet2x from './images/hp-home-banner-tablet-@2x.jpg';
import bannerDesktop from './images/hp-home-banner-@1x.jpg';
import bannerDesktop2x from './images/hp-home-banner-@2x.jpg';

import step1 from './images/how-step1.png';
import step2 from './images/how-step2.svg';
import step3 from './images/how-step3.svg';

import LoadAsync from '_platform/src/utils/LoadAsync';

const VisaProvider = LoadAsync(() =>
  import(/* webpackChunkName: "VisaProvider" */ '../VisaProvider/VisaProvider')
);
const StatementVisaBalance = LoadAsync(() =>
  import(
    /* webpackChunkName: "statementLoyalty" */ '../../components/StatementVisa/StatementVisaBalance'
  )
);

const HomeContainer = styled.div`
  height: 100%;
`;

const AchieveContainer = styled.section`
  background-color: rgba(43, 65, 90, 0.3);
  color: #2b415a;
`;

const HomePage = ({ settings }) => {
  // const testVisa = {
  //   cards: [
  //     {
  //       /*
  //       cardMask: 'xxxxxxxxxxxx1234',
  //       expiryDate: '10/27',
  //       cardHolder: 'Matt Smith',
  //       cardBalance: 100000,
  //       transactions: [
  //         {
  //           transactionId: 'xyz',
  //           transferDate: '24/05/2025',
  //           debitAmount: '123',
  //           balanceAmount: '100000',
  //           description: 'Dollars used',
  //         },
  //         {
  //           transactionId: 'abc',
  //           transferDate: '21/05/2025',
  //           creditAmount: '100123',
  //           balanceAmount: '100123',
  //           description: 'Dollars loaded onto Visa Card',
  //         },
  //       ],
  //     */
  //     },
  //   ],
  // };

  return (
    <HomeContainer>
      <Helmet>
        {/* <meta name="description" content="Page description" /> */}
      </Helmet>
      <PrivateComponent
        redirectOnError={{
          pathname: settings.settingsApp.loginPagePath || '/login/',
        }}
      >
        <BannerHome
          pageTitle={
            <h1>
              Welcome to
              <br />
              <strong>HP Rewards</strong>
            </h1>
          }
          pageText={
            <>
              <PrivateComponent deniedRoles={['Allocator']}>
                <p className="text--tight">
                  A new, streamlined way to reward our Partners for your sales.
                  New look, new features and an overall smoother process for our
                  Partners to be rewarded.
                </p>
                <p className="text--tight">
                  Your company’s Allocator will receive dollars for monthly
                  sales and they allocate these dollars among the Participants
                  within your company.
                </p>
                <p className="text--tight">
                  Once you are allocated your first HP Rewards dollars, you will
                  receive your HP Rewards Visa Card with these funds pre-loaded.
                </p>
                <p className="text--tight">
                  If you are an existing HP Rewards Visa Cardholder, your
                  dollars will automatically be loaded to your card within 24hrs
                  of you receiving your dollars.
                </p>
              </PrivateComponent>
              <PrivateComponent deniedRoles={['NonAllocator']}>
                <p className="text--tight">
                  A new, streamlined way to reward our Partners for your sales.
                  New look, new features and an overall smoother process for our
                  Partners to be rewarded.
                </p>
                <p className="text--tight">
                  As you are your company’s nominated Allocator, you are in
                  charge of allocating HP Reward dollars among the Participants
                  within your company.
                </p>
                <p className="text--tight">
                  Whenever new HP Reward dollars are awarded to your company,
                  you will receive an email notification stating that new
                  dollars are available for allocation.
                </p>
                <p className="text--tight">
                  Please be sure to allocate all dollars for all months that the
                  HP Rewards dollars relate to!
                </p>
              </PrivateComponent>
            </>
          }
          bannerMobile={bannerMobile}
          bannerMobile2x={bannerMobile2x}
          bannerTablet={bannerTablet}
          bannerTablet2x={bannerTablet2x}
          bannerDesktop={bannerDesktop}
          bannerDesktop2x={bannerDesktop2x}
        />
        <PrivateComponent deniedRoles={['Allocator']}>
          <BgSecondary>
            <VisaProvider
              fetch={[
                {
                  endpoint: '/Statement/v1/Knect/',
                  section: 'visa',
                },
              ]}
              render={({ visa }) =>
                !visa || !visa.data ? (
                  <LoadingAnimation /> // Loader instead of `null`?
                ) : (
                  <>
                    {visa &&
                      visa.data &&
                      visa.data.visa &&
                      visa.data.visa.cards &&
                      visa.data.visa.cards.map(card => (
                        <StatementVisaBalance card={card} key={card.cardMask} />
                      ))}
                  </>
                )
              }
            />
          </BgSecondary>
          <AchieveContainer>
            <Grid>
              <Row justifyContent="center" className="spacer--top">
                <Col className="text--center">
                  <h2>How to Earn</h2>
                </Col>
              </Row>
              <Row justifyContent="center" className="spacer--bottom">
                <Col sm={6} lg={3}>
                  <IconWrapper>
                    <img src={step1} alt="" />
                  </IconWrapper>
                  <h3 className="text--center text--tight">Step 1</h3>
                  <p className="text--center text--large">
                    Dollars are allocated to you
                  </p>
                </Col>
                <Col sm={6} lg={3}>
                  <IconWrapper>
                    <img src={step2} alt="" />
                  </IconWrapper>
                  <h3 className="text--center text--tight">Step 2</h3>
                  <p className="text--center text--large">
                    Dollars will be loaded to your card within 24hrs
                  </p>
                </Col>
                <Col sm={6} lg={3}>
                  <IconWrapper>
                    <img src={step3} alt="" />
                  </IconWrapper>
                  <h3 className="text--center text--tight">Step 3</h3>
                  <p className="text--center text--large">
                    Spend wherever Visa is accepted
                  </p>
                </Col>
              </Row>
            </Grid>
          </AchieveContainer>
        </PrivateComponent>
      </PrivateComponent>
    </HomeContainer>
  );
};

HomePage.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default withSettings(HomePage);
